// Shared formatting helpers. Extracted so list-style pages (Players,
// Leaderboards, …) share one implementation instead of drifting copies.

const DATE_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

/**
 * Compact relative time — "just now", "2m ago", "2h ago", "1d ago", or
 * "MMM d, yyyy" past 7 days. Returns "—" when the input is empty or invalid.
 * Used by the Updated column of list-style pages (mockup: "2h ago").
 */
export function formatRelativeShort(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return DATE_FMT.format(d);
}

const DOW_NAMES: Record<string, string> = {
  "0": "Sunday", "7": "Sunday",
  "1": "Monday", "2": "Tuesday", "3": "Wednesday",
  "4": "Thursday", "5": "Friday", "6": "Saturday",
  SUN: "Sunday", MON: "Monday", TUE: "Tuesday", WED: "Wednesday",
  THU: "Thursday", FRI: "Friday", SAT: "Saturday",
};

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

interface FieldSpec {
  name: string;
  min: number;
  max: number;
  unitSingular: string;
  unitPlural: string;
  aliases?: Record<string, number>;
  ordinalize?: boolean;
}

const CRON_FIELDS: FieldSpec[] = [
  { name: "minute", min: 0, max: 59, unitSingular: "minute", unitPlural: "minutes" },
  { name: "hour", min: 0, max: 23, unitSingular: "hour", unitPlural: "hours" },
  {
    name: "day-of-month",
    min: 1,
    max: 31,
    unitSingular: "day",
    unitPlural: "days",
    ordinalize: true,
  },
  {
    name: "month",
    min: 1,
    max: 12,
    unitSingular: "month",
    unitPlural: "months",
    aliases: {
      JAN: 1, FEB: 2, MAR: 3, APR: 4, MAY: 5, JUN: 6,
      JUL: 7, AUG: 8, SEP: 9, OCT: 10, NOV: 11, DEC: 12,
    },
  },
  {
    name: "day-of-week",
    min: 0,
    max: 7,
    unitSingular: "day-of-week",
    unitPlural: "days-of-week",
    aliases: {
      SUN: 0, MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, SAT: 6,
    },
  },
];

function atomName(s: string, spec: FieldSpec): string {
  const alias = resolveAlias(s, spec);
  if (alias !== null) {
    if (spec.name === "month") return MONTH_NAMES[alias - 1];
    if (spec.name === "day-of-week") return DOW_NAMES[String(alias === 7 ? 0 : alias)] ?? s;
    return String(alias);
  }
  if (/^\d+$/.test(s)) {
    const n = Number(s);
    if (spec.name === "month") return MONTH_NAMES[n - 1];
    if (spec.name === "day-of-week") return DOW_NAMES[String(n === 7 ? 0 : n)] ?? String(n);
    return spec.ordinalize ? ordinal(n) : String(n);
  }
  return s;
}

function joinList(items: string[]): string {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}

function describeField(value: string, spec: FieldSpec): string {
  if (value.includes(",")) {
    return joinList(value.split(",").map((p) => describeField(p, spec)));
  }
  const stepMatch = value.match(/^(.+)\/(\d+)$/);
  if (stepMatch) {
    const [, base, stepStr] = stepMatch;
    const step = Number(stepStr);
    if (base === "*") return `every ${step} ${spec.unitPlural}`;
    if (base.includes("-")) {
      const [a, b] = base.split("-");
      return `every ${step} ${spec.unitPlural} from ${atomName(a, spec)} through ${atomName(b, spec)}`;
    }
    return `every ${step} ${spec.unitPlural} starting from ${atomName(base, spec)}`;
  }
  if (value.includes("-")) {
    const [a, b] = value.split("-");
    return `${atomName(a, spec)} through ${atomName(b, spec)}`;
  }
  if (value === "*") return `every ${spec.unitSingular}`;
  return atomName(value, spec);
}

function resolveAlias(value: string, spec: FieldSpec): number | null {
  const k = value.toUpperCase();
  if (spec.aliases && k in spec.aliases) return spec.aliases[k];
  return null;
}

function validateAtom(atom: string, spec: FieldSpec): string | null {
  if (atom === "*") return null;
  const alias = resolveAlias(atom, spec);
  if (alias !== null) return null;
  if (!/^\d+$/.test(atom)) {
    return `${spec.name}: "${atom}" is not a valid value`;
  }
  const n = Number(atom);
  if (n < spec.min || n > spec.max) {
    return `${spec.name}: ${n} out of range ${spec.min}-${spec.max}`;
  }
  return null;
}

interface FieldResult {
  ok: boolean;
  complex: boolean;
  reason?: string;
}

function validateField(value: string, spec: FieldSpec): FieldResult {
  if (value === "") return { ok: false, complex: false, reason: `${spec.name}: empty field` };
  if (value === "*") return { ok: true, complex: false };

  // List: a,b,c — each item validated independently. Any item complex → complex.
  if (value.includes(",")) {
    let complex = true;
    for (const item of value.split(",")) {
      const r = validateField(item, spec);
      if (!r.ok) return r;
      complex = complex || r.complex;
    }
    return { ok: true, complex };
  }

  // Step: base/N (base is "*" or a range).
  if (value.includes("/")) {
    const idx = value.indexOf("/");
    const base = value.slice(0, idx);
    const stepStr = value.slice(idx + 1);
    if (!/^\d+$/.test(stepStr)) {
      return { ok: false, complex: false, reason: `${spec.name}: invalid step "${stepStr}"` };
    }
    const step = Number(stepStr);
    if (step < 1) {
      return { ok: false, complex: false, reason: `${spec.name}: step must be ≥ 1` };
    }
    const baseR = validateField(base || "*", spec);
    if (!baseR.ok) return baseR;
    return { ok: true, complex: true };
  }

  // Range: a-b.
  if (value.includes("-")) {
    const [a, b] = value.split("-");
    const aErr = validateAtom(a, spec);
    if (aErr) return { ok: false, complex: false, reason: aErr };
    const bErr = validateAtom(b, spec);
    if (bErr) return { ok: false, complex: false, reason: bErr };
    return { ok: true, complex: true };
  }

  // Single atom or alias.
  const err = validateAtom(value, spec);
  if (err) return { ok: false, complex: false, reason: err };
  return { ok: true, complex: false };
}

function describeFullCron(parts: string[]): string {
  const [min, hr, dom, mon, dow] = parts;

  const pieces: string[] = [];

  // Time prefix — pick the most natural English form by case.
  const minIsInt = /^\d+$/.test(min);
  const hrIsInt = /^\d+$/.test(hr);

  if (minIsInt && hrIsInt) {
    pieces.push(`At ${pad2(Number(hr))}:${pad2(Number(min))}`);
  } else if (min === "*" && hr === "*") {
    pieces.push("Every minute");
  } else if (hr === "*") {
    const stepMatch = min.match(/^\*\/(\d+)$/);
    if (stepMatch) pieces.push(`Every ${stepMatch[1]} minutes`);
    else if (minIsInt) pieces.push(`At minute ${min} of every hour`);
    else pieces.push(`At ${describeField(min, CRON_FIELDS[0])} past every hour`);
  } else if (min === "*") {
    pieces.push(`Every minute of hour ${describeField(hr, CRON_FIELDS[1])}`);
  } else if (minIsInt) {
    pieces.push(`At minute ${min} of hour ${describeField(hr, CRON_FIELDS[1])}`);
  } else {
    pieces.push(
      `At ${describeField(min, CRON_FIELDS[0])} past hour ${describeField(hr, CRON_FIELDS[1])}`,
    );
  }

  if (dom !== "*") {
    const desc = describeField(dom, CRON_FIELDS[2]);
    pieces.push(desc.startsWith("every ") ? desc : `on the ${desc} of the month`);
  }
  if (mon !== "*") {
    const desc = describeField(mon, CRON_FIELDS[3]);
    pieces.push(desc.startsWith("every ") ? desc : `in ${desc}`);
  }
  if (dow !== "*") {
    const desc = describeField(dow, CRON_FIELDS[4]);
    pieces.push(desc.startsWith("every ") ? desc : `on ${desc}`);
  }

  return pieces.join(", ");
}

/**
 * Result of analysing a cron expression for the admin UI hint.
 *
 *  - `empty` — input was blank.
 *  - `ok` — syntactically valid; `text` carries the human description for
 *    any standard 5-field expression (atoms, lists, ranges, steps).
 *  - `invalid` — fails syntax / range checks. UI shows an error.
 */
export type CronAnalysis =
  | { kind: "empty" }
  | { kind: "ok"; text: string }
  | { kind: "invalid"; reason: string };

/** Analyse a standard 5-field cron expression for the admin form hint. */
export function analyseCron(cron: string | null | undefined): CronAnalysis {
  if (!cron) return { kind: "empty" };
  const trimmed = cron.trim();
  if (!trimmed) return { kind: "empty" };
  const parts = trimmed.split(/\s+/);
  if (parts.length !== 5) {
    return {
      kind: "invalid",
      reason: `Expected 5 space-separated fields, got ${parts.length}`,
    };
  }
  for (let i = 0; i < 5; i++) {
    const r = validateField(parts[i], CRON_FIELDS[i]);
    if (!r.ok) return { kind: "invalid", reason: r.reason ?? "Invalid cron syntax" };
  }
  return { kind: "ok", text: describeFullCron(parts) };
}

/**
 * Convenience wrapper that returns the description string for valid crons
 * and an empty string otherwise. Use analyseCron directly when you need
 * to distinguish invalid from valid.
 */
export function humaniseCron(cron: string | null | undefined): string {
  const r = analyseCron(cron);
  return r.kind === "ok" ? r.text : "";
}
